export type ShortcutPlatform = "mac" | "windows" | "linux"

interface ParsedShortcut {
  key: string
  shift: boolean
  alt: boolean
  ctrl: boolean
  meta: boolean
  mod: boolean
}

const MODIFIER_ALIASES: Record<string, keyof Omit<ParsedShortcut, "key">> = {
  shift: "shift",
  alt: "alt",
  option: "alt",
  ctrl: "ctrl",
  control: "ctrl",
  meta: "meta",
  cmd: "meta",
  command: "meta",
  mod: "mod",
}

const MAC_MODIFIER_LABELS: Record<Exclude<keyof ParsedShortcut, "key" | "mod">, string> = {
  shift: "⇧",
  alt: "⌥",
  ctrl: "⌃",
  meta: "⌘",
}

const OTHER_MODIFIER_LABELS: Record<Exclude<keyof ParsedShortcut, "key" | "mod">, string> = {
  shift: "Shift",
  alt: "Alt",
  ctrl: "Ctrl",
  meta: "Meta",
}

const MODIFIER_ORDER: Array<Exclude<keyof ParsedShortcut, "key" | "mod">> = ["ctrl", "alt", "shift", "meta"]

const normalizeToken = (token: string) => token.trim().toLowerCase()

export const getShortcutPlatform = (): ShortcutPlatform => {
  if (typeof navigator === "undefined") {
    return "windows"
  }

  const navigatorWithUAData = navigator as Navigator & { userAgentData?: { platform?: string } }
  const userPlatform = navigatorWithUAData.userAgentData?.platform || navigator.platform || ""
  const normalized = userPlatform.toLowerCase()

  if (normalized.includes("mac")) {
    return "mac"
  }
  if (normalized.includes("win")) {
    return "windows"
  }
  return "linux"
}

export const parseShortcut = (shortcut: string): ParsedShortcut => {
  const parsed: ParsedShortcut = {
    key: "",
    shift: false,
    alt: false,
    ctrl: false,
    meta: false,
    mod: false,
  }

  for (const segment of shortcut.split("+")) {
    const token = normalizeToken(segment)
    const modifier = MODIFIER_ALIASES[token]
    if (modifier) {
      parsed[modifier] = true
      continue
    }
    parsed.key = token
  }

  return parsed
}

const normalizeKey = (key: string) => {
  if (key.length === 1) {
    return key.toLowerCase()
  }
  return key.trim().toLowerCase()
}

export const doesShortcutMatch = (
  event: KeyboardEvent,
  shortcut: string,
  platform: ShortcutPlatform,
): boolean => {
  const parsed = parseShortcut(shortcut)
  if (!parsed.key) {
    return false
  }

  const expectsMetaFromMod = parsed.mod && platform === "mac"
  const expectsCtrlFromMod = parsed.mod && platform !== "mac"

  const expectedMeta = parsed.meta || expectsMetaFromMod
  const expectedCtrl = parsed.ctrl || expectsCtrlFromMod

  if (
    event.metaKey !== expectedMeta ||
    event.ctrlKey !== expectedCtrl ||
    event.altKey !== parsed.alt ||
    event.shiftKey !== parsed.shift
  ) {
    return false
  }

  return normalizeKey(event.key) === normalizeKey(parsed.key)
}

const formatKeyLabel = (key: string): string => {
  if (key === ",") return ","
  if (key.length === 1) return key.toUpperCase()
  return key[0].toUpperCase() + key.slice(1)
}

export const formatShortcutLabel = (shortcut: string, platform: ShortcutPlatform): string => {
  const parsed = parseShortcut(shortcut)
  const labels = platform === "mac" ? MAC_MODIFIER_LABELS : OTHER_MODIFIER_LABELS
  const activeModifiers: Array<Exclude<keyof ParsedShortcut, "key" | "mod">> = [...MODIFIER_ORDER]

  if (parsed.mod) {
    if (platform === "mac") {
      activeModifiers.push("meta")
    } else {
      activeModifiers.push("ctrl")
    }
  }

  const uniqueModifiers = Array.from(new Set(activeModifiers)).filter((modifier) => parsed[modifier])
  const modifierLabels = uniqueModifiers.map((modifier) => labels[modifier])
  const keyLabel = formatKeyLabel(parsed.key)

  if (platform === "mac") {
    return [...modifierLabels, keyLabel].join("")
  }

  return [...modifierLabels, keyLabel].join("+")
}

export const isEventTargetEditable = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null
  if (!element) return false

  if (element.isContentEditable) {
    return true
  }

  const tag = element.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
}

